import React, {useEffect} from 'react';
import {View} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {observer} from 'mobx-react';

import Text from '../../components/ui-controls/Text';
import {useSearchDispatch} from '../../context/SearchContext';
import {AppRoute} from '../../models/AppRoute';
import {Department} from '../../models/middleware';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {navigateToGiftCardsPromotionOrLoadingScreen, usePromotions} from '../../queries/promotions';
import {GIFT_CARDS_TILE, SPECIALS_TILE} from '../../stores/rangeStore';
import {useStores} from '../../stores/use-stores';
import {logger} from '../../utils/logger';
import {openDepartment} from '../../utils/openDepartment';

import RangeProductListScreen from './RangeProductListScreen';
import RangeSubDepartmentsScreen from './RangeSubDepartmentsScreen';

const TmpScreen = ({text}: {text: string}) => (
  <View>
    <Text>{text}</Text>
  </View>
);

const DepartmentScreen = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.DEPARTMENT>) => {
    const {rangeStore, filterStore} = useStores();
    const {rawPromotions} = usePromotions();
    const searchDispatch = useSearchDispatch();

    // DIS-2048: a beta crash read `department` from undefined params. The trigger is not known.
    const {department, specials} = route.params ?? {};

    if (!department) {
      return <TmpScreen text="Category not found" />;
    }

    // When navigating directly to /shop/categories/gift-cards
    //
    // DIS-1567: this dependency was `promotionsStore`, an immortal object, so the
    // effect fired once per focus. `rawPromotions` changes identity whenever the
    // gated set actually changes (login, logout, store switch, content-changing
    // refetch). Kept deliberately rather than narrowed to the gift-cards promotion
    // id: `useFocusEffect` only invokes the callback when `navigation.isFocused()`,
    // and the first invocation navigates away, so a later identity change cannot
    // re-fire the navigation from a blurred screen. `usePromotions` memoises its
    // `select`, so identity does not churn per render.
    useFocusEffect(
      React.useCallback(() => {
        logger.debug('DepartmentScreen', 'Department focused', {departmentId: department.id});
        if (department.id === GIFT_CARDS_TILE) {
          navigateToGiftCardsPromotionOrLoadingScreen(rawPromotions, navigation.navigate, false);
        }
      }, [department.id, navigation.navigate, rawPromotions]),
    );

    if (department.id === GIFT_CARDS_TILE) {
      return <TmpScreen text="Loading your Gift Cards..." />;
    }

    // Store mutations MUST be in useEffect, not during render
    useEffect(() => {
      rangeStore.selectDepartment(department);
      filterStore.clearSortAndFilters();
      if (specials) {
        // Department-scoped Specials link: no sub-department tiles, product list only.
        rangeStore.selectSubDepartment(department);
        filterStore.loadFilterOptions();
        searchDispatch({type: 'SCOPE_TO_DEPARTMENT', departmentId: department.id, specials: true});
        return;
      }
      const subDepartments = rangeStore.getSubDepartments(department.id);
      if (!subDepartments.length) {
        rangeStore.selectSubDepartment(department);
        filterStore.loadFilterOptions();
        if (department.id === SPECIALS_TILE) {
          searchDispatch({type: 'SCOPE_TO_DEPARTMENT', departmentId: '', specials: true});
        } else {
          searchDispatch({type: 'SCOPE_TO_DEPARTMENT', departmentId: department.id});
        }
      } else {
        searchDispatch({type: 'SET_DEPARTMENT', departmentId: department.id});
      }
    }, [rangeStore, filterStore, department, specials, searchDispatch]);

    // Re-establish department scope when this screen regains focus.
    // SearchScreen's CLEAR_ALL resets departmentId to null in the shared
    // SearchContext.  When the user navigates back here, the mount useEffect
    // above does NOT re-fire (same route params), leaving departmentId null.
    // SET_DEPARTMENT (not SCOPE_TO_DEPARTMENT) preserves user-applied filters.
    useFocusEffect(
      React.useCallback(() => {
        if (specials) {
          searchDispatch({type: 'SET_DEPARTMENT', departmentId: department.id});
          searchDispatch({type: 'SET_SPECIALS', enabled: true, pinned: true});
          return;
        }
        const subDepartments = rangeStore.getSubDepartments(department.id);
        if (!subDepartments.length) {
          if (department.id === SPECIALS_TILE) {
            searchDispatch({type: 'SET_DEPARTMENT', departmentId: ''});
            searchDispatch({type: 'SET_SPECIALS', enabled: true, pinned: true});
          } else {
            searchDispatch({type: 'SET_DEPARTMENT', departmentId: department.id});
          }
        } else {
          searchDispatch({type: 'SET_DEPARTMENT', departmentId: department.id});
        }
      }, [department.id, specials, rangeStore, searchDispatch]),
    );

    const handleSubDepartmentPress = (subDepartment: Department) =>
      openDepartment(
        {rangeStore, filterStore, searchDispatch, rawPromotions, navigate: navigation.navigate},
        {department: subDepartment, parent: department},
      );

    const subDepartments = specials ? [] : rangeStore.getSubDepartments(department.id);
    if (subDepartments.length > 0) {
      subDepartments.unshift({
        ...department,
        name: 'View All',
      });
      return (
        <RangeSubDepartmentsScreen
          department={department}
          subDepartments={subDepartments}
          onSubDepartmentPress={handleSubDepartmentPress}
        />
      );
    } else {
      // TODO: This depends on  rangeStore.selected... 🙄
      return <RangeProductListScreen />;
    }
  },
);

export default DepartmentScreen;
